import React, { useEffect, useState, useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    Box,
    InputBase,
    InputAdornment,
    IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import UserApi from '../../../../rest-api/user-api';
import BlockingSpinner from '../../../UIControls/BlockingSpinner';
import { useSnackbar } from '../../SnackbarProvider';
import { scaleLoadedObjects } from './scaleLoadedObjects';

/**
 * PROPS:
 *  - open: Boolean, whether modal is open
 *  - onClose: Function, close handler
 *  - onOpenFile: Function, callback with the selected entry (canvasData, notes, etc.)
 *  - handoutData: data of the current handout (to filter whiteboard data by handout)
 *  - canvasRef: canvas generated in WhiteboardCanvas
 */
const LoadWhiteboardModal = ({ open, onClose, onOpenFile, handoutData, canvasRef, onSetShowLabels }) => {
    const location = 'en-GB';
    const { userDataStore, authStore } = useContext(StoreContext);
    const [entries, setEntries] = useState([]);
    const [sortMethod, setSortMethod] = useState('dateDesc'); // 'dateDesc' | 'patientName'
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [selectedNotes, setSelectedNotes] = useState('');
    const [loadingFile, setLoadingFile] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const showSnackbar = useSnackbar();

    const isStudent = userDataStore?.subscriptionData?.accountType === 'Student';
    // const isStudent = true;

    // Fetch the user’s saved whiteboard files once the dialog opens
    useEffect(() => {
        if (open) {
            fetchWhiteboardEntries();
        }
    }, [open]);

    useEffect(() => {
        const handler = (e) => {
            const data = e.detail;
            console.warn('[AutoLoad] Trying to match whiteboardId:', data.whiteboardId);

            const match = entries.find((entry) => entry.whiteboardId === data.whiteboardId);
            if (match) {
                console.warn('[AutoLoad] Match found! Auto-loading...');
                setSelectedEntry(match);
                setSelectedNotes(match.notes || '');

                setTimeout(() => {
                    handleLoad();
                }, 100);
            } else {
                console.warn('[AutoLoad] No match found in entries');
            }
        };

        window.addEventListener('loadWhiteboardFromMyLibrary', handler);
        return () => window.removeEventListener('loadWhiteboardFromMyLibrary', handler);
    }, [entries]);

    const fetchWhiteboardEntries = async () => {
        try {
            console.warn('Calling user-api fetchWhiteboardEntries');

            const handoutId = handoutData.contentId;
            const response = await UserApi.getWhiteboardEntries(
                authStore.idToken,
                handoutId ? 'handoutId' : null,
                handoutId || null,
            );

            setEntries(response.whiteboards || []);
        } catch (err) {
            console.error('Failed to fetch whiteboard entries:', err);
        }
    };

    const getFilteredAndSortedEntries = () => {
        const filtered = entries.filter((entry) => {
            const query = searchQuery.toLowerCase();
            const dateStr = entry.lastUpdated ? new Date(entry.lastUpdated).toLocaleString() : '';

            return (
                (entry.updatedBy || '').toLowerCase().includes(query) ||
                (entry.updatedByFirstName || '').toLowerCase().includes(query) ||
                (entry.updatedByLastName || '').toLowerCase().includes(query) ||
                dateStr.toLowerCase().includes(query) ||
                (entry.notes || '').toLowerCase().includes(query) ||
                (entry.patientName || '').toLowerCase().includes(query) ||
                (entry.clientSurname || '').toLowerCase().includes(query) ||
                (entry.clinicPatientId || '').toLowerCase().includes(query) ||
                (entry.microchipNumber || '').toLowerCase().includes(query)
            );
        });

        const sortBy = (arr, fn) => [...arr].sort(fn);

        switch (sortMethod) {
            case 'dateAsc':
                return sortBy(filtered, (a, b) => (a.lastUpdated || 0) - (b.lastUpdated || 0));
            case 'dateDesc':
                return sortBy(filtered, (a, b) => (b.lastUpdated || 0) - (a.lastUpdated || 0));
            case 'patientNameAsc':
                return sortBy(filtered, (a, b) =>
                    (a.patientName || '').localeCompare(b.patientName || '', undefined, { sensitivity: 'base' }),
                );
            case 'patientNameDesc':
                return sortBy(filtered, (a, b) =>
                    (b.patientName || '').localeCompare(a.patientName || '', undefined, { sensitivity: 'base' }),
                );
            case 'clientSurnameAsc':
                return sortBy(filtered, (a, b) =>
                    (a.clientSurname || '').localeCompare(b.clientSurname || '', undefined, {
                        sensitivity: 'base',
                    }),
                );
            case 'clientSurnameDesc':
                return sortBy(filtered, (a, b) =>
                    (b.clientSurname || '').localeCompare(a.clientSurname || '', undefined, {
                        sensitivity: 'base',
                    }),
                );
            case 'authorAsc':
                return sortBy(filtered, (a, b) =>
                    (a.updatedBy || '').localeCompare(b.updatedBy || '', undefined, { sensitivity: 'base' }),
                );
            case 'authorDesc':
                return sortBy(filtered, (a, b) =>
                    (b.updatedBy || '').localeCompare(a.updatedBy || '', undefined, { sensitivity: 'base' }),
                );
            case 'clinicPatientIdAsc':
                return sortBy(filtered, (a, b) =>
                    (a.clinicPatientId || '').localeCompare(b.clinicPatientId || '', undefined, {
                        sensitivity: 'base',
                    }),
                );
            case 'clinicPatientIdDesc':
                return sortBy(filtered, (a, b) =>
                    (b.clinicPatientId || '').localeCompare(a.clinicPatientId || '', undefined, {
                        sensitivity: 'base',
                    }),
                );
            default:
                return filtered;
        }
    };

    const handleSelectRow = (entry) => {
        setSelectedEntry(entry);
        setSelectedNotes(entry.notes || '');
    };

    // New handleLoad: when user clicks "Open", we
    //  1. Validate selection.
    //  2. Use the selected record's canvasJsonLink to get a presigned GET URL.
    //  3. Show spinner while fetching the JSON.
    //  4. Fetch the JSON data from S3.
    //  5. Load the JSON into the Fabric canvas.
    //  6. Call onOpenFile callback and close the modal.
    const handleLoad = async () => {
        if (!selectedEntry) {
            showSnackbar('Please select a file first.', 'error');
            return;
        }

        if (selectedEntry.diagramVariant) {
            if (selectedEntry.diagramVariant === 'withLabels') {
                onSetShowLabels(true);
            } else {
                onSetShowLabels(false);
            }
        }

        setLoadingFile(true);

        try {
            let jsonData;

            if (selectedEntry.canvasData) {
                jsonData = JSON.parse(selectedEntry.canvasData);
            } else if (selectedEntry.canvasJsonLink) {
                const payload = {
                    contentCategory: 'whiteboard',
                    files: [{ method: 'get', key: selectedEntry.canvasJsonLink }],
                };

                const presignedResp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                if (!presignedResp.getUrls || presignedResp.getUrls.length === 0) {
                    throw new Error('No presigned URL received.');
                }

                const getUrl = presignedResp.getUrls[0].presignedUrl;
                const response = await fetch(getUrl);
                if (!response.ok) {
                    throw new Error(`Failed to fetch whiteboard JSON: ${response.statusText}`);
                }
                jsonData = await response.json();
            } else {
                throw new Error('No canvas data or JSON link found in the selected entry.');
            }

            const oldW = jsonData.canvasWidth || 0;
            const oldH = jsonData.canvasHeight || 0;
            const newW = canvasRef.current.getWidth();
            const newH = canvasRef.current.getHeight();

            canvasRef.current.loadFromJSON(jsonData, () => {
                setTimeout(() => {
                    const objects = canvasRef.current.getObjects();

                    if (objects.length > 0) {
                        scaleLoadedObjects(canvasRef.current, oldW, oldH, newW, newH);
                    } else {
                        console.warn('No objects found after loading JSON. Skipping scaling.');
                    }

                    canvasRef.current.requestRenderAll();
                    canvasRef.current.calcOffset();
                    window.dispatchEvent(new Event('resize'));

                    // Ensure objects are within bounds before rendering
                    canvasRef.current.getObjects().forEach((obj) => {
                        obj.left = Math.max(0, Math.min(newW - obj.width * obj.scaleX, obj.left));
                        obj.top = Math.max(0, Math.min(newH - obj.height * obj.scaleY, obj.top));
                        obj.setCoords();
                    });

                    canvasRef.current.renderAll();

                    // If whiteboard, store it in localStorage.
                    if (selectedEntry.whiteboardId && selectedEntry.canvasImageLink) {
                        localStorage.setItem(
                            'lastWhiteboardState',
                            JSON.stringify({
                                whiteboardId: selectedEntry.whiteboardId,
                                handoutId: selectedEntry.handoutId,
                                canvasImageLink: selectedEntry.canvasImageLink,
                                fileName: selectedEntry.fileName || '',
                                eTag: selectedEntry.eTag || null,
                                ...(selectedEntry.updatedByFirstName && {
                                    updatedByFirstName: selectedEntry.updatedByFirstName,
                                }),
                                ...(selectedEntry.updatedByLastName && {
                                    updatedByLastName: selectedEntry.updatedByLastName,
                                }),
                                ...(selectedEntry.diagramVariant && { diagramVariant: selectedEntry.diagramVariant }),
                            }),
                        );

                        console.warn(
                            'Whiteboard data stored to localStorage:',
                            localStorage.getItem('lastWhiteboardState'),
                        );
                    }

                    showSnackbar('Whiteboard successfully loaded!', 'success');
                    onOpenFile(selectedEntry);
                    onClose();
                }, 50);
            });
        } catch (error) {
            console.error('Error loading whiteboard:', error);
            showSnackbar('Failed to load whiteboard. Please try again.', 'error');
        } finally {
            setLoadingFile(false); // Spinner stops even if an error occurs
        }
    };

    const toggleSort = (type) => {
        setSortMethod((prev) => {
            if (prev.startsWith(type)) {
                return prev.endsWith('Asc') ? `${type}Desc` : `${type}Asc`;
            } else {
                return `${type}Asc`;
            }
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Load Whiteboard</DialogTitle>
            <DialogContent
                sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    height: { xs: '80vh', md: 600 },
                    position: 'relative',
                }}
            >
                {/* Spinner while loading file */}
                {loadingFile && <BlockingSpinner open={true} />}
                {/* Sticky Search + Table Header as one block */}
                {/* Entire table: sticky top for search + header */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={isStudent ? 2 : 6} sx={{ backgroundColor: '#fff' }}>
                                    {/* Search input inside header cell */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                        <InputBase
                                            placeholder="Search whiteboard..."
                                            value={searchQuery}
                                            onChange={(e) => {
                                                setSearchQuery(e.target.value);
                                                setSelectedEntry(null);
                                                setSelectedNotes('');
                                            }}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton>
                                                        <SearchIcon sx={{ color: searchQuery ? 'black' : '#555' }} />
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            sx={{
                                                width: { xs: '100%', sm: '60%', md: '280px' },
                                                maxWidth: '100%',
                                                px: 1.5,
                                                py: 0.75,
                                                borderRadius: 8,
                                                border: '1px solid #ccc',
                                                fontSize: '0.875rem',
                                                height: '40px',
                                                color: 'black',
                                                backgroundColor: '#fff',
                                                '& .MuiInputBase-input': {
                                                    padding: 0,
                                                },
                                            }}
                                        />
                                    </Box>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell onClick={() => toggleSort('handoutTitle')} sx={{ cursor: 'pointer' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>Handout Title</Box>
                                </TableCell>
                                <TableCell onClick={() => toggleSort('date')} sx={{ cursor: 'pointer' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        Updated On
                                        {sortMethod.startsWith('date') && (
                                            <Box component="span" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                                                {sortMethod === 'dateAsc' ? '▲' : '▼'}
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                                {!isStudent && (
                                    <>
                                        <TableCell onClick={() => toggleSort('author')} sx={{ cursor: 'pointer' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                Author
                                                {sortMethod.startsWith('author') && (
                                                    <Box component="span" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                                                        {sortMethod === 'authorAsc' ? '▲' : '▼'}
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell onClick={() => toggleSort('patientName')} sx={{ cursor: 'pointer' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                Patient
                                                {sortMethod.startsWith('patientName') && (
                                                    <Box component="span" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                                                        {sortMethod === 'patientNameAsc' ? '▲' : '▼'}
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            onClick={() => toggleSort('clientSurname')}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                Client Surname
                                                {sortMethod.startsWith('clientSurname') && (
                                                    <Box component="span" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                                                        {sortMethod === 'clientSurnameAsc' ? '▲' : '▼'}
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            onClick={() => toggleSort('clinicPatientId')}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                Clinic Patient ID
                                                {sortMethod.startsWith('clinicPatientId') && (
                                                    <Box component="span" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                                                        {sortMethod === 'clinicPatientIdAsc' ? '▲' : '▼'}
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {getFilteredAndSortedEntries().map((entry) => {
                                const dateStr = entry.lastUpdated
                                    ? new Date(entry.lastUpdated).toLocaleString(location)
                                    : 'N/A';
                                const author =
                                    entry.updatedByFirstName || entry.updatedByLastName
                                        ? `${entry.updatedByFirstName || ''} ${entry.updatedByLastName || ''}`.trim()
                                        : entry.updatedBy || 'Unknown';

                                return (
                                    <TableRow
                                        key={entry.fileName}
                                        hover
                                        selected={selectedEntry?.fileName === entry.fileName}
                                        onClick={() => handleSelectRow(entry)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>{entry.handoutContentName || 'Untitled'}</TableCell>
                                        <TableCell>{dateStr}</TableCell>
                                        {!isStudent && <TableCell>{author}</TableCell>}
                                        {!isStudent && <TableCell>{entry.patientName || 'N/A'}</TableCell>}
                                        {!isStudent && <TableCell>{entry.clientSurname || 'N/A'}</TableCell>}
                                        {!isStudent && <TableCell>{entry.clinicPatientId || 'N/A'}</TableCell>}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>

                {/* Sticky Bottom Notes */}
                {selectedEntry && (
                    <Box
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 2,
                            backgroundColor: '#fff',
                            borderTop: '1px solid #eee',
                            px: 2,
                            pt: 2,
                            pb: 2,
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Notes for Selected File
                        </Typography>
                        <TextField
                            value={selectedNotes}
                            multiline
                            rows={3}
                            fullWidth
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <Button onClick={handleLoad} variant="contained" color="primary">
                    Open
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LoadWhiteboardModal;
